/**
 * Polls Investigator runs from ABOVE the router, so navigating does not stop
 * the wait.
 *
 * The poll used to live inside a chat component's own state, which was lost
 * whenever that component unmounted, so a user who moved from the drug page to
 * search never learned their run had finished. Mounted above `<Routes>`, this
 * survives every in-app navigation; the watch list survives reload through
 * sessionStorage.
 *
 * It never attaches a result: attaching is the user's explicit choice (the
 * panel offer, or Use in CEPI). It only reports the outcome and match count.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { fetchJobProgress } from '../services/activeResult';
import { useAuth } from './AuthContext';
import {
  addWatched, loadWatched, outcomeOf, removeWatched, saveWatched,
  type FinishedJob, type JobProgress, type WatchedJob,
} from '../services/watchedJobs';

const POLL_MS = 5000;

interface JobWatcherValue {
  /** Start waiting on a run. Safe to call twice for the same job. */
  watch: (job: Omit<WatchedJob, 'startedAt'>) => void;
  /** Runs still in flight. Present from the click, before the first poll. */
  running: WatchedJob[];
  /**
   * Live progress per job id, refreshed by the poll that was already running.
   * Absent for a job whose first tick has not returned — the caller says
   * "Starting…" rather than waiting for a line to exist.
   */
  progress: Record<string, JobProgress>;
  /** Runs that finished and have not been dismissed. */
  finished: FinishedJob[];
  dismiss: (jobId: string) => void;
}

const JobWatcherContext = createContext<JobWatcherValue | null>(null);

export const JobWatcherProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [watched, setWatched] = useState<WatchedJob[]>(() => loadWatched(Date.now()));
  const [finished, setFinished] = useState<FinishedJob[]>([]);
  const [progress, setProgress] = useState<Record<string, JobProgress>>({});
  // Guards against a slow poll overlapping the next tick and settling twice.
  const inFlight = useRef<Set<string>>(new Set());

  useEffect(() => { saveWatched(watched); }, [watched]);

  // Watched runs belong to the signed-in user. When the uid goes away or
  // changes (sign-out, or another account in this tab), drop them so the next
  // user's tray never polls or announces the previous user's jobs; the effect
  // above then clears storage too. Starts null, so restoring a session on page
  // load (null -> uid) is not a change — same rule as AuthContext's purge.
  const uid = useAuth().user?.uid ?? null;
  const lastUid = useRef<string | null>(null);
  useEffect(() => {
    if (lastUid.current !== null && lastUid.current !== uid) {
      setWatched([]);
      setFinished([]);
      setProgress({});
      inFlight.current.clear();
    }
    lastUid.current = uid;
  }, [uid]);

  const watch = useCallback((job: Omit<WatchedJob, 'startedAt'>) => {
    setWatched((prev) => addWatched(prev, job, Date.now()));
  }, []);

  const dismiss = useCallback((jobId: string) => {
    setFinished((prev) => prev.filter((j) => j.jobId !== jobId));
  }, []);

  useEffect(() => {
    if (!watched.length) return undefined;
    let cancelled = false;

    const settle = async (job: WatchedJob) => {
      if (inFlight.current.has(job.jobId)) return;
      inFlight.current.add(job.jobId);
      try {
        const live = await fetchJobProgress(job.jobId);
        if (cancelled) return;
        // Keep the phase even when the run is not over: this tick is the only
        // thing that knows what it is doing, and it used to be discarded.
        // `null` is a network blip, not a state — leave the last line standing
        // rather than blanking the display on one failed fetch.
        if (live) setProgress((prev) => ({ ...prev, [job.jobId]: live }));
        const outcome = outcomeOf(live?.status ?? null);
        if (!outcome) return;               // still running

        // Only the count is read; `found` is the run's own tally, so no
        // thread is needed and nothing is attached.
        const matched = outcome === 'complete' ? live?.found : undefined;

        setWatched((prev) => removeWatched(prev, job.jobId));
        // The finished toast carries the outcome from here on; a lingering
        // progress entry would print the same job twice mid-transition.
        setProgress((prev) => {
          if (!(job.jobId in prev)) return prev;
          const next = { ...prev };
          delete next[job.jobId];
          return next;
        });
        setFinished((prev) => (
          prev.some((j) => j.jobId === job.jobId)
            ? prev
            : [...prev, { ...job, outcome, matched }]
        ));
      } finally {
        inFlight.current.delete(job.jobId);
      }
    };

    const tick = () => { watched.forEach((job) => { void settle(job); }); };
    tick();
    const timer = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [watched]);

  return (
    <JobWatcherContext.Provider
      value={{ watch, running: watched, progress, finished, dismiss }}
    >
      {children}
    </JobWatcherContext.Provider>
  );
};

/**
 * Null outside the provider rather than throwing.
 *
 * Components are mounted by tests and by surfaces that do not wrap the
 * app shell; a throw here would turn "no watcher available" into a blank
 * page, which is a worse failure than a run that is simply not watched.
 */
export function useJobWatcher(): JobWatcherValue | null {
  return useContext(JobWatcherContext);
}

export default JobWatcherProvider;
